import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { GoogleAuthButton } from "../components/GoogleAuthButton";
import AuthShell from "../components/AuthShell";
import { setPageTitle } from "../lib/format";

export default function Register() {
  const { register, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [searchParams] = useSearchParams();
  const [refCode, setRefCode] = useState(searchParams.get("ref") || "");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  // A visitor who is ALREADY logged in gets bounced to the dashboard — but a user
  // who just registered here must proceed to the reward reveal, not be raced away
  // by this same effect.
  const justRegistered = useRef(false);

  useEffect(() => setPageTitle("Create Account"), []);
  useEffect(() => { if (user && !justRegistered.current) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    justRegistered.current = true;
    try {
      const res = await register(email.trim(), password, name.trim(), refCode.trim() || undefined);
      toast.success("Account created — welcome to MyRedPark");
      navigate(res?.fresh_signup || res?.show_reward ? "/welcome/reward" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testId="register-page" title="Create your account" subtitle="Find parking — or earn from yours."
      footer={
        <div className="space-y-5 text-center">
          <div className="flex items-center gap-4" aria-hidden="true">
            <div className="h-px flex-1 bg-[#1F2328]" /><span className="text-[0.68rem] uppercase tracking-[0.18em] text-zinc-600">or</span><div className="h-px flex-1 bg-[#1F2328]" />
          </div>
          <GoogleAuthButton testId="google-register-btn" text="signup_with" onRewardRoute={() => { justRegistered.current = true; }} />
          <p className="text-sm text-zinc-500">
            Already have an account? <Link to="/login" data-testid="go-login-link" className="text-[#FF3B45] font-semibold hover:underline">Log in</Link>
          </p>
          <p className="text-xs text-zinc-600">
            By creating an account you agree to our <Link to="/terms" className="underline hover:text-zinc-300">Terms of Service</Link> and <Link to="/privacy" className="underline hover:text-zinc-300">Privacy Policy</Link>.
          </p>
        </div>
      }>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <label htmlFor="reg-name" className="label-dark">Full name</label>
          <input id="reg-name" data-testid="register-name-input" required autoComplete="name"
                 value={name} onChange={(e) => setName(e.target.value)} className="input-dark" placeholder="Alex Driver" />
        </div>
        <div>
          <label htmlFor="reg-email" className="label-dark">Email</label>
          <input id="reg-email" data-testid="register-email-input" type="email" required autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" placeholder="you@example.com" />
        </div>
        <div>
          <label htmlFor="reg-password" className="label-dark">Password</label>
          <input id="reg-password" data-testid="register-password-input" type="password" required autoComplete="new-password" minLength={8}
                 value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark" placeholder="At least 8 characters" />
        </div>
        <div>
          <label htmlFor="reg-ref" className="label-dark">Referral code <span className="text-zinc-600">(optional)</span></label>
          <input id="reg-ref" data-testid="register-referral-input" value={refCode} maxLength={10}
                 onChange={(e) => setRefCode(e.target.value.toUpperCase())} className="input-dark font-mono tracking-widest" placeholder="MRP-XXXXXX" />
        </div>
        <button type="submit" data-testid="register-submit-btn" disabled={busy} className="btn-primary w-full !h-12">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
        </button>
      </form>
    </AuthShell>
  );
}
